// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

export interface LeaderboardRow {
  rank: number;
  organizationName: string;
  organizationLogo?: string;
  influenceScore: number;
  influenceScoreLevel?: string;
  isCurrentOrg: boolean;
}

export interface LeaderboardConfig {
  title: string;
  columns: LeaderboardColumn[];
}

export interface LeaderboardColumn {
  name: string;
  prop: string;
  columnWidth?: string;
  style?: Record<string, string>;
  dataStyle?: Record<string, string>;
}

export class BaseLeaderboardEntity {
  static readonly COLUMNS: LeaderboardColumn[] = [
    {
      name: '#',
      prop: 'rank',
      columnWidth: '80px',
      style: { 'text-align': 'center' }
    },
    {
      name: 'Organization',
      prop: 'name',
      columnWidth: '300px'
    },
    {
      name: 'Influence Score',
      prop: 'influenceScoreLevel',
      columnWidth: '150px',
      style: { 'text-align': 'right', 'text-transform': 'capitalize' },
      dataStyle: { 'text-align': 'right' }
    }
  ];

  static filterLeaderboardData(
    data: LeaderboardRow[],
    currentOrgName: string
  ): LeaderboardRow[] {
    if (!data || data.length === 0) {
      return [];
    }

    const currentOrgIndex = data.findIndex(
      row => row.organizationName.toLowerCase() === currentOrgName.toLowerCase()
    );

    if (currentOrgIndex === -1 || currentOrgIndex < 10) {
      return data.slice(0, 10);
    }

    const top9 = data.slice(0, 9);
    const currentOrg = data[currentOrgIndex];

    return [...top9, currentOrg];
  }

  static filterLeaderboardDataByFlag(data: LeaderboardRow[]): LeaderboardRow[] {
    if (!data || data.length === 0) {
      return [];
    }

    const currentOrgIndex = data.findIndex(row => row.isCurrentOrg);

    if (currentOrgIndex === -1 || currentOrgIndex < 10) {
      return data.slice(0, 10);
    }

    const top9 = data.slice(0, 9);
    const currentOrg = data[currentOrgIndex];

    return [...top9, currentOrg];
  }

  static markCurrentOrganization(
    data: LeaderboardRow[],
    currentOrgName: string
  ): LeaderboardRow[] {
    return data.map(row => ({
      ...row,
      isCurrentOrg: row.organizationName.toLowerCase() === currentOrgName.toLowerCase()
    }));
  }

  static prepareLeaderboardData(
    data: LeaderboardRow[],
    currentOrgName: string
  ): LeaderboardRow[] {
    const markedData = this.markCurrentOrganization(data, currentOrgName);
    return this.filterLeaderboardData(markedData, currentOrgName);
  }
}

